/**
 * The last stage: the engine has chosen a TABLE for every chair; this chooses the
 * SEAT. Everything here is either the host's seat pattern or the seeded PRNG —
 * there is no name-based ordering.
 *
 *  - Parties (and 'must' keep-together units) stay physically together: a unit's
 *    chairs are one contiguous run of ordinals, kept on one side of a rectangle
 *    when a run that does so exists.
 *  - The order the units sit in, and the order of the parties inside a unit, is
 *    shuffled — otherwise the first chair of every table would be whoever the
 *    engine happened to place first.
 *  - Seat pattern (Across / One seat apart) applies to PAIRS only: a unit of
 *    exactly two chairs — a guest and their plus-one, or two guests joined by a
 *    must-together link. A unit of three or more stays contiguous ("side by
 *    side"): asking a household of four to sit "across" has no sensible meaning
 *    beyond its pairs, and the pairs inside it are not separately linked.
 *  - 'Anywhere' scatters every chair over the free seats of its table.
 *
 * A pattern is never allowed to fail a seating. If the table's shape cannot give
 * it (an odd round table has no exact across; a three-seater has no gap) the
 * pair takes the nearest seats it can, and if no free seats fit at all it sits
 * side by side; either way the preview says so.
 */
import { partnerSeats, sameSide, type SeatPattern } from './seatRelation';
import type { Random } from './seededRandom';
import type { Party, Placed, Unit } from './autoSeatTypes';
import type { SeatingTable } from '../types/seating';

const PATTERN_TEXT: Record<SeatPattern, string> = {
  side_by_side: 'side by side',
  across: 'across from each other',
  one_apart: 'one seat apart',
  anywhere: 'anywhere',
};

function freeSeats(table: SeatingTable, taken: Set<number>): number[] {
  const out: number[] = [];
  for (let i = 0; i < table.capacity; i += 1) if (!taken.has(i)) out.push(i);
  return out;
}

/** The lowest run of consecutive free chairs, preferring one on a single side; else the lowest free ones. */
function contiguousRun(table: SeatingTable, taken: Set<number>, count: number): number[] {
  const free = freeSeats(table, taken);
  const runs: number[][] = [];
  for (let start = 0; start + count <= free.length; start += 1) {
    if (free[start + count - 1] - free[start] === count - 1) runs.push(free.slice(start, start + count));
  }
  return runs.find((run) => sameSide(table, run)) ?? runs[0] ?? free.slice(0, count);
}

/** A unit's chairs, party by party, with the parties in a random order. */
function chairsInOrder(chairs: Placed[], random: Random): Placed[] {
  const byParty = new Map<Party, Placed[]>();
  for (const chair of chairs) byParty.set(chair.party, [...(byParty.get(chair.party) ?? []), chair]);
  return random.shuffle([...byParty.values()]).flat();
}

export interface SeatPassResult {
  seatOf: Map<Placed, number>;
  warnings: string[];
}

export function assignSeats(input: {
  tables: SeatingTable[];
  placed: Placed[];
  /** Chairs already held per table: kept chairs, blocked chairs, locked chairs. */
  taken: Map<string, Set<number>>;
  pattern: SeatPattern;
  random: Random;
}): SeatPassResult {
  const { pattern, random } = input;
  const seatOf = new Map<Placed, number>();
  const notes = new Map<string, { label: string; kind: 'inexact' | 'nospace'; pairs: number }>();

  for (const table of input.tables) {
    const here = input.placed.filter((chair) => chair.table.id === table.id);
    if (here.length === 0) continue;
    const taken = new Set(input.taken.get(table.id) ?? []);
    const byUnit = new Map<Unit, Placed[]>();
    for (const chair of here) byUnit.set(chair.unit, [...(byUnit.get(chair.unit) ?? []), chair]);
    const units = random.shuffle([...byUnit.entries()]);
    const put = (chair: Placed, seat: number) => {
      seatOf.set(chair, seat);
      taken.add(seat);
    };
    const putRun = (chairs: Placed[]) => {
      contiguousRun(table, taken, chairs.length).forEach((seat, i) => put(chairs[i], seat));
    };
    const isPair = ([, chairs]: [Unit, Placed[]]) =>
      chairs.length === 2 && (pattern === 'across' || pattern === 'one_apart');

    // Pairs first: they need two specific chairs, and a run of plain chairs would fragment them.
    for (const entry of units.filter(isPair)) {
      const [first, second] = chairsInOrder(entry[1], random);
      const options: Array<{ a: number; b: number; exact: boolean }> = [];
      for (const a of freeSeats(table, taken)) {
        const { seats, exact } = partnerSeats(table, a, pattern);
        for (const b of seats) if (!taken.has(b)) options.push({ a, b, exact });
      }
      const exactOptions = options.filter((option) => option.exact);
      const pool = exactOptions.length > 0 ? exactOptions : options;
      if (pool.length > 0) {
        const chosen = random.pick(pool);
        put(first, chosen.a);
        put(second, chosen.b);
        if (!chosen.exact) {
          const note = notes.get(`${table.id}|inexact`) ?? { label: table.label, kind: 'inexact' as const, pairs: 0 };
          notes.set(`${table.id}|inexact`, { ...note, pairs: note.pairs + 1 });
        }
      } else {
        putRun([first, second]);
        const note = notes.get(`${table.id}|nospace`) ?? { label: table.label, kind: 'nospace' as const, pairs: 0 };
        notes.set(`${table.id}|nospace`, { ...note, pairs: note.pairs + 1 });
      }
    }

    for (const entry of units.filter((candidate) => !isPair(candidate))) {
      const chairs = chairsInOrder(entry[1], random);
      if (pattern === 'anywhere') {
        for (const chair of chairs) put(chair, random.pick(freeSeats(table, taken)));
      } else {
        putRun(chairs);
      }
    }
  }

  const warnings = [...notes.values()].map(({ label, kind, pairs }) => {
    const noun = `${pairs} pair${pairs === 1 ? '' : 's'}`;
    return kind === 'inexact'
      ? `${label || 'A table'} is a shape that can’t seat people exactly ${PATTERN_TEXT[pattern]}, so ${noun} sit as close to that as it allows.`
      : `${label || 'A table'} had no free chairs left for ${PATTERN_TEXT[pattern]}, so ${noun} sit side by side instead.`;
  });
  return { seatOf, warnings };
}
